import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Check, Sparkles, Lock, Gift, Star, MessageCircle, Heart,
  ShieldCheck, Users, MapPin, ClipboardList, BookOpen, Search, Smile,
  HeartHandshake, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import usePageMeta from "@/hooks/usePageMeta";
import { faqJsonLd, breadcrumbJsonLd, SITE_URL } from "@/lib/jsonld";

// Outcome-focused benefit bullets — replaces feature lists
const FREE = [
  "One Hope Chest for the loved one in your care",
  "Track medications, appointments, and important contacts",
  "Write care notes you can find again in seconds",
  "Hope Planner — a gentle weekly view of what's ahead",
  "Free forever, no credit card required",
];

const PREMIUM = [
  "Add a Hope Chest for every loved one in your care",
  "Everyone stays on the same page, even from different cities",
  "Keep family members informed without repeating yourself",
  "Capture important updates instantly without typing",
  "Never miss important appointments or caregiving tasks",
  "Keep critical documents secure and easy to find when needed",
  "A printable PDF binder for the ER bag or doctor's visit",
  "Care Mode — a calm, large-text view your loved one can use",
  "Educational guides written for family caregivers",
  "Priority support from a real person within 24 hours",
];

const EMOTIONAL_BENEFITS = [
  { icon: Smile, title: "Reduce caregiving stress", body: "Stop carrying every detail in your head. Let Hope Chest hold what you need so you can be present." },
  { icon: ClipboardList, title: "Stay prepared for appointments", body: "Walk into the doctor's office with the meds list, recent symptoms, and questions ready." },
  { icon: Users, title: "Keep family members informed", body: "Siblings, spouses, aides — everyone sees the same updates without a single group text." },
  { icon: Search, title: "Stop searching for information", body: "Insurance cards, medication doses, contacts — all in one place when you need them most." },
  { icon: ShieldCheck, title: "Build confidence managing care", body: "When you're organized, every conversation with a doctor, nurse, or family member feels steadier." },
  { icon: HeartHandshake, title: "Everything in one place", body: "Medical, legal, financial, daily care. One trusted home instead of seven sticky notes." },
];

const WHO_ITS_FOR = [
  { icon: Heart, title: "Adult children", body: "Caring for an aging parent — often from a distance — while balancing work and family of your own." },
  { icon: Users, title: "Spouses & partners", body: "Walking alongside someone you love through illness, recovery, or aging in place." },
  { icon: ClipboardList, title: "Family caregivers", body: "Managing medications, appointments, and the daily texture of someone else's well-being." },
  { icon: MapPin, title: "Long-distance families", body: "Coordinating care with siblings or relatives across cities, time zones, or countries." },
];

const FAQ_ITEMS = [
  {
    q: "Is the Free plan really free forever?",
    a: "Yes. The Free plan is permanently free for one Hope Chest — no trial expiring, no credit card needed, no surprise charges. We make money when families need to coordinate across multiple caregivers, and we'd rather earn that trust than pressure you into a paywall.",
  },
  {
    q: "What's the difference between Free and Premium?",
    a: "Free gives one person everything they need to organize care for one loved one. Premium is for families — it adds shared access (so your sister or spouse sees the same updates you do), unlimited Hope Chests if you're caring for more than one person, SMS reminders, the printable PDF binder for ER visits, and the weekly family report so you stop having to repeat yourself.",
  },
  {
    q: "Can I share access with my siblings or my mom's home aide?",
    a: "Yes — that's what Premium was built for. You can invite family members as co-caregivers (full access) or care professionals as view-only (like a home aide who needs the meds list but not financial info). Everyone sees the same up-to-date information without group texts or shared spreadsheets.",
  },
  {
    q: "Is my information private and secure?",
    a: "Always. Your data is encrypted in transit and at rest. We never sell or share it. Only the family members and professionals you specifically invite can see your Hope Chest, and you can revoke access at any time. You can also export everything you've added and delete your account whenever you choose.",
  },
  {
    q: "What if I'm not tech-savvy?",
    a: "Hope Chest was built for caregivers, not engineers. If you can text a photo or open an email, you can use it. The onboarding checklist walks you through the first few steps, and you can email support@organizedhope.online any time — a real person reads every message.",
  },
  {
    q: "What happens if I cancel Premium?",
    a: "Nothing is lost. Your account stays active on the Free plan and all your data — medications, appointments, notes, contacts — stays exactly where it is. You'll just lose the Premium-only features (family sharing, SMS, PDF export) until you re-subscribe.",
  },
  {
    q: "Do I have to add a credit card to try Premium?",
    a: "No. Every new account starts with a 14-day Premium trial — no credit card, no payment info. After 14 days, you automatically move to the Free plan unless you choose to subscribe. You'll never be charged by surprise.",
  },
  {
    q: "What is a 'Founding Member'?",
    a: "The first 100 caregivers to subscribe lock in $14.99/month forever. As we add new Premium features (push notifications, voice notes, multi-language, more), they all land in your chest at no extra cost. After 100 spots are claimed, the founding offer closes and pricing returns to normal.",
  },
  {
    q: "Can I use Hope Chest on my phone?",
    a: "Yes. Hope Chest works in any browser on iPhone, Android, tablets, and computers. You can add it to your home screen for one-tap access just like a native app.",
  },
  {
    q: "Who built this?",
    a: "Hope Chest was built by Michael — a family caregiver who got tired of juggling sticky notes, group texts, and ten browser tabs while trying to coordinate care. Every feature exists because a real caregiver said \"I wish I had a place for this.\" If you have an idea, email me — I read every message.",
  },
];

const FOUNDING_PERKS = [
  { icon: Lock, text: "Price locked for life — even when we raise prices for everyone else" },
  { icon: Gift, text: "All future Premium features free — no upsells, no add-ons, ever" },
  { icon: Star, text: "Founding Member badge on your profile" },
  { icon: MessageCircle, text: "A direct line to Michael — email me back any time" },
  { icon: Heart, text: "Help build Hope Chest for a family who needs it" },
];

export default function Pricing() {
  usePageMeta({
    title: "Pricing — Hope Chest for Family Caregivers",
    description: "Free forever for one loved one. Premium $14.99/month for families coordinating care across siblings, spouses, and care teams. Built by a caregiver, for caregivers.",
    canonical: `${SITE_URL}/pricing`,
    jsonLd: [
      faqJsonLd(FAQ_ITEMS),
      breadcrumbJsonLd([
        { name: "Home", url: "/" },
        { name: "Pricing", url: "/pricing" },
      ]),
    ],
  });
  const { user } = useAuth();
  const nav = useNavigate();
  const [packages, setPackages] = useState(null);
  const [founding, setFounding] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/payments/packages").then((r) => setPackages(r.data)).catch(() => {});
    api.get("/public/founding-status").then((r) => setFounding(r.data)).catch(() => {});
  }, []);

  const subscribe = async (pkg, isFounding = false) => {
    if (!user) {
      try { sessionStorage.setItem("hc_pending_checkout", JSON.stringify({ pkg, founding: isFounding })); } catch (e) { /* ignore */ }
      nav("/signup");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: pkg,
        origin_url: window.location.origin,
        founding: isFounding,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const foundingOpen = founding?.is_open !== false;
  const spotsLeft = founding?.spots_remaining ?? 100;
  const spotsTaken = founding?.spots_taken ?? 0;
  const nextNumber = founding?.next_founder_number ?? (spotsTaken + 1);
  const countdownActive = founding?.countdown_active;
  const daysRemaining = founding?.days_remaining;
  const isAlreadyFounder = user?.founding_member;

  const scrollToPlans = () => {
    document.getElementById("plans")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goSignup = () => nav(user ? "/dashboard" : "/signup");

  const bannerMessage = countdownActive && daysRemaining != null
    ? (
      <>
        <strong>{daysRemaining === 1 ? "Last day" : `${daysRemaining} days left`}</strong>
        {" "}to become Founding Member <strong>#{nextNumber}</strong> — lock in $14.99/month for life
      </>
    )
    : (
      <>
        You&apos;d be Founding Member <strong>#{nextNumber}</strong> · {spotsLeft} of {founding?.total_spots ?? 100} spots remaining
      </>
    );

  return (
    <div className="min-h-screen bg-[#FCFAF8] text-[#3D3935] relative overflow-hidden">
      <div className="hc-blob" style={{ background: "#D8E2D8", width: 500, height: 500, top: -150, right: -100 }} />
      <div className="hc-blob" style={{ background: "#E8DCC8", width: 380, height: 380, bottom: 200, left: -120, opacity: 0.4 }} />

      {/* Founding member banner */}
      {foundingOpen && !isAlreadyFounder && (
        <div className="relative z-10 bg-[#3A5A4A] text-white" data-testid="founding-banner">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex items-center justify-center gap-3 text-sm">
            <Sparkles className="w-4 h-4 flex-shrink-0" />
            <span className="font-medium">
              {bannerMessage} — <a href="#founding" className="underline underline-offset-2 hover:text-[#E8DCC8]">claim your spot</a>
            </span>
          </div>
        </div>
      )}

      <nav className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="pricing-back-home">
          <img src="/hope-chest-logo.png" alt="Hope Chest" className="w-10 h-10 object-contain" />
          <span className="font-brand text-xl font-semibold">Hope Chest</span>
        </Link>
        <Button onClick={() => nav(user ? "/dashboard" : "/login")} variant="ghost" className="rounded-full" data-testid="pricing-nav-cta">
          {user ? "Dashboard" : "Sign in"}
        </Button>
      </nav>

      {/* ====== HERO ====== */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-16 text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E] mb-4">For family caregivers</p>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.1]" data-testid="pricing-hero-headline">
          The caregiving organization system I wish I had from day one.
        </h1>
        <p className="mt-6 text-lg text-[#7A736E] max-w-2xl mx-auto">
          Try Hope Chest free and discover a simpler way to organize care for the people who matter most.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            onClick={goSignup}
            className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] px-7 py-6 text-base font-medium"
            data-testid="pricing-hero-primary-cta"
          >
            Get started free
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button
            onClick={scrollToPlans}
            variant="outline"
            className="rounded-full border-[#EBE5DF] text-[#3D3935] px-7 py-6 text-base"
            data-testid="pricing-hero-secondary-cta"
          >
            View Premium features
          </Button>
        </div>
        <p className="text-xs text-[#7A736E] mt-4">No credit card required · 14-day Premium trial included</p>
      </section>

      {/* ====== "CAREGIVING IS HARD ENOUGH" ====== */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="hc-card p-10 md:p-14 bg-gradient-to-br from-[#F3EFEA] to-white border-[#E8DCC8]">
          <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E] mb-3">Why Hope Chest exists</p>
          <h2 className="font-display text-3xl md:text-4xl font-semibold leading-tight mb-5" data-testid="pricing-why-headline">
            Caregiving is hard enough. Staying organized shouldn&apos;t be.
          </h2>
          <div className="space-y-4 text-[#3D3935] text-[16px] leading-relaxed">
            <p>
              When you&apos;re caring for someone you love, every day brings a new piece of information to remember — a new medication, a new specialist, a new question to ask the doctor. Most of it ends up on sticky notes, in group texts, or in the back of your mind at 2 a.m.
            </p>
            <p>
              Hope Chest was built by a family caregiver who got tired of juggling all of it alone. It&apos;s a quiet, private place to keep the medical, legal, financial, and daily care details for someone you love — and to share what matters with the family members and professionals helping you carry the weight.
            </p>
            <p className="text-[#3A5A4A] italic">
              You shouldn&apos;t need a project-management degree to take care of your mom. You just need a place for everything to live, and a way to keep your family on the same page.
            </p>
          </div>
        </div>
      </section>

      {/* ====== EMOTIONAL BENEFITS ====== */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="pricing-benefits-section">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E] mb-3">What changes when you use Hope Chest</p>
          <h2 className="font-display text-3xl md:text-4xl font-semibold">
            Less in your head. More in your hands.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {EMOTIONAL_BENEFITS.map(({ icon: Icon, title, body }) => (
            <div key={title} className="hc-card p-6" data-testid={`benefit-${title.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="w-11 h-11 rounded-xl bg-[#E8DCC8] flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-[#3A5A4A]" />
              </div>
              <h3 className="font-display text-lg mb-2">{title}</h3>
              <p className="text-sm text-[#7A736E] leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ====== WHO IT'S FOR ====== */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="pricing-who-section">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E] mb-3">Built for the people doing the caring</p>
          <h2 className="font-display text-3xl md:text-4xl font-semibold">
            Who Hope Chest is for.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {WHO_ITS_FOR.map(({ icon: Icon, title, body }) => (
            <div key={title} className="hc-card p-6 flex flex-col">
              <Icon className="w-6 h-6 text-[#C87B67] mb-3" />
              <h3 className="font-display text-lg mb-2 leading-tight">{title}</h3>
              <p className="text-sm text-[#7A736E] leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ====== TRUST — BUILT BY A CAREGIVER ====== */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="hc-card p-10 md:p-14 text-center bg-gradient-to-br from-white to-[#F3EFEA]" data-testid="pricing-trust-section">
          <div className="w-14 h-14 rounded-full bg-[#C87B67] mx-auto mb-5 flex items-center justify-center">
            <Heart className="w-6 h-6 text-white" fill="white" />
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold mb-5">
            Built from real caregiving — not a boardroom.
          </h2>
          <p className="text-[#3D3935] text-[16px] leading-relaxed max-w-2xl mx-auto mb-3">
            Hope Chest was created from real caregiving experience and designed to help families navigate this season with greater clarity, confidence, and peace of mind.
          </p>
          <p className="text-[#7A736E] text-[15px] leading-relaxed max-w-2xl mx-auto">
            Every feature exists because a caregiver said, &quot;I wish I had a place for this.&quot; We&apos;re a small team that picks up emails personally and ships improvements every week based on what real families ask for.
          </p>
        </div>
      </section>

      {/* ====== FOUNDING MEMBER (unchanged structurally) ====== */}
      {foundingOpen && !isAlreadyFounder && (
        <section id="founding" className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 py-12 scroll-mt-12">
          <div
            className="relative rounded-3xl p-8 md:p-12 overflow-hidden bg-gradient-to-br from-[#3A5A4A] via-[#2C4538] to-[#1F3128] text-white shadow-2xl"
            data-testid="founding-card"
          >
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#C87B67] opacity-15 blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-[#E8DCC8] opacity-10 blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Sparkles className="w-4 h-4 text-[#E8DCC8]" />
                <span className="text-xs uppercase tracking-[0.2em] text-[#E8DCC8]">
                  {countdownActive && daysRemaining != null
                    ? (daysRemaining === 1 ? "Closes tomorrow" : `Closes in ${daysRemaining} days`)
                    : "Limited — only 100 spots"}
                </span>
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-[#E8DCC8] text-[#2C4538] font-semibold">
                  You&apos;d be #{nextNumber}
                </span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
                Become a Founding Member
              </h2>
              <p className="mt-4 text-white/80 leading-relaxed max-w-2xl">
                The first chapter of Organized Hope is being written — and we want you in it.
                Join Hope Chest Premium today and lock that rate in for life. As we grow, every
                new feature lands in your chest at no extra cost. Ever.
              </p>
              <div className="grid md:grid-cols-[1.4fr_1fr] gap-8 mt-8 items-start">
                <ul className="space-y-3" data-testid="founding-perks">
                  {FOUNDING_PERKS.map(({ icon: Icon, text }, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-white/90">
                      <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon className="w-3.5 h-3.5 text-[#E8DCC8]" />
                      </div>
                      <span>{text}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                  <p className="text-xs uppercase tracking-wider text-[#E8DCC8]">Founding rate</p>
                  <p className="font-display text-4xl font-semibold mt-1">
                    $14.99<span className="text-base text-white/60 font-normal"> /month</span>
                  </p>
                  <p className="text-sm text-white/70 mt-1">or $99/year — save 45%</p>
                  <div className="grid grid-cols-2 gap-2 mt-5">
                    <Button onClick={() => subscribe("monthly", true)} disabled={busy || user?.subscription_tier === "paid"} className="rounded-full bg-[#E8DCC8] hover:bg-white text-[#2C4538] font-semibold" data-testid="founding-monthly-cta">Monthly</Button>
                    <Button onClick={() => subscribe("yearly", true)} disabled={busy || user?.subscription_tier === "paid"} className="rounded-full bg-white hover:bg-[#E8DCC8] text-[#2C4538] font-semibold" data-testid="founding-yearly-cta">Yearly</Button>
                  </div>
                  <div className="mt-4 flex items-center gap-2 text-xs text-white/70">
                    <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full bg-[#E8DCC8] transition-all" style={{ width: `${Math.min(100, (spotsTaken / (founding?.total_spots ?? 100)) * 100)}%` }} />
                    </div>
                    <span className="font-medium text-[#E8DCC8] whitespace-nowrap">{spotsLeft} left</span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-white/50 mt-6 italic">We&apos;ll only accept 100 founders. After that, the door closes — and standard pricing applies.</p>
            </div>
          </div>
        </section>
      )}

      {isAlreadyFounder && (
        <section className="relative z-10 max-w-3xl mx-auto px-6 md:px-12 py-12">
          <div className="rounded-3xl p-8 bg-gradient-to-br from-[#E8DCC8] to-[#F3EFEA] border-2 border-[#3A5A4A]/20 text-center" data-testid="founding-thank-you-card">
            <Sparkles className="w-10 h-10 text-[#3A5A4A] mx-auto mb-3" />
            <h2 className="font-display text-2xl font-semibold">You&apos;re Founding Member #{user.founding_member_number || "?"}</h2>
            <p className="text-[#7A736E] mt-2 max-w-md mx-auto">Your rate is locked in for life. All future Premium features land in your chest at no extra cost. Thank you for helping build this.</p>
          </div>
        </section>
      )}

      {/* ====== PRE-PRICING CTA ====== */}
      <section id="plans" className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 pt-12 pb-6 text-center scroll-mt-12">
        <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E] mb-3">Plans for every caregiver</p>
        <h2 className="font-display text-3xl md:text-4xl font-semibold">
          Start free. Bring family in when you&apos;re ready.
        </h2>
        <p className="text-[#7A736E] mt-3 max-w-xl mx-auto">
          Both plans keep your data safe and private. The difference is how many people can help you carry it.
        </p>
      </section>

      {/* ====== PLAN COMPARISON ====== */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 py-8 pb-16 grid md:grid-cols-2 gap-6">
        {/* FREE PLAN */}
        <div className="hc-card p-8" data-testid="pricing-free-card">
          <p className="text-xs uppercase tracking-wider text-[#7A736E]">Free</p>
          <h3 className="font-brand text-3xl mt-2">Hope Chest</h3>
          <p className="text-[#7A736E] mt-1">Everything you need to begin organizing care with confidence.</p>
          <p className="font-display text-5xl font-semibold mt-6">
            $0<span className="text-base text-[#7A736E] font-normal"> / forever</span>
          </p>
          <Button
            onClick={() => nav(user ? "/dashboard" : "/signup")}
            variant="outline"
            className="w-full mt-6 rounded-full border-[#EBE5DF]"
            data-testid="pricing-free-cta"
          >
            {user?.subscription_tier === "free" ? "Your current plan" : "Get started free"}
          </Button>
          <p className="text-xs text-[#7A736E] text-center mt-2">No credit card required</p>
          <ul className="mt-8 space-y-3">
            {FREE.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm">
                <Check className="w-4 h-4 text-[#3A5A4A] mt-0.5 flex-shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* PREMIUM PLAN */}
        <div className={`hc-card p-8 relative ${foundingOpen && !isAlreadyFounder ? "" : "border-2 border-[#3A5A4A]"}`} data-testid="pricing-premium-card">
          {(!foundingOpen || isAlreadyFounder) && (
            <span className="absolute -top-3 left-8 px-3 py-1 rounded-full text-xs uppercase tracking-wider bg-[#3A5A4A] text-white">
              Recommended for families
            </span>
          )}
          <p className="text-xs uppercase tracking-wider text-[#7A736E]">Premium</p>
          <h3 className="font-display text-3xl mt-2">Hope Chest+</h3>
          <p className="text-[#7A736E] mt-1">Keep your entire family informed, organized, and connected.</p>
          {packages && (
            <>
              <p className="font-display text-5xl font-semibold mt-6">
                ${packages.monthly.amount}<span className="text-base text-[#7A736E] font-normal"> / month</span>
              </p>
              <p className="text-sm text-[#7A736E]">or ${packages.yearly.amount}/year — save 45%</p>
            </>
          )}
          <div className="grid grid-cols-2 gap-2 mt-6">
            <Button
              onClick={() => subscribe("monthly", false)}
              disabled={busy || user?.subscription_tier === "paid"}
              className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]"
              data-testid="pricing-monthly-cta"
            >
              {user?.subscription_tier === "paid" ? "Active" : "Monthly"}
            </Button>
            <Button
              onClick={() => subscribe("yearly", false)}
              disabled={busy || user?.subscription_tier === "paid"}
              variant="outline"
              className="rounded-full border-[#3A5A4A] text-[#3A5A4A]"
              data-testid="pricing-yearly-cta"
            >
              {user?.subscription_tier === "paid" ? "Active" : "Yearly"}
            </Button>
          </div>
          <p className="text-xs text-[#7A736E] text-center mt-2">14-day free trial · Cancel anytime</p>
          <ul className="mt-8 space-y-3">
            {PREMIUM.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm">
                <Check className="w-4 h-4 text-[#3A5A4A] mt-0.5 flex-shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ====== FAQ ====== */}
      <section className="relative z-10 max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-20" data-testid="pricing-faq-section">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E] mb-3">Common questions</p>
          <h2 className="font-display text-3xl md:text-4xl font-semibold">
            Everything caregivers ask before they begin.
          </h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3" data-testid="pricing-faq-accordion">
          {FAQ_ITEMS.map((item, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="hc-card px-6 border-0"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="font-display text-lg text-left hover:no-underline py-5">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#3D3935] leading-relaxed text-[15px] pb-5">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-10 text-center">
          <p className="text-sm text-[#7A736E] mb-4">Still have questions? We&apos;d love to help.</p>
          <Link to="/support" className="inline-flex items-center gap-1.5 text-sm text-[#3A5A4A] font-medium hover:underline" data-testid="pricing-faq-support-link">
            Email a real person <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>

      {/* ====== EDUCATION TEASE ====== */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 pb-16">
        <div className="hc-card p-8 md:p-10 bg-[#3A5A4A] text-white text-center" data-testid="pricing-resources-cta">
          <BookOpen className="w-8 h-8 text-[#E8DCC8] mx-auto mb-4" />
          <h2 className="font-display text-2xl md:text-3xl font-semibold mb-3">
            New to caregiving? We have a guide for that.
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-6">
            Plain-language explanations of caregiving vocabulary (POA vs. healthcare proxy, ADLs, advance directives) and practical guides for the first 30 days.
          </p>
          <Link
            to="/resources"
            className="inline-flex items-center gap-2 rounded-full bg-[#E8DCC8] text-[#2C4538] hover:bg-white px-6 py-3 text-sm font-medium transition"
            data-testid="pricing-resources-link"
          >
            Open the Caregiver Resources <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ====== FINAL CTA ====== */}
      <section className="relative z-10 max-w-3xl mx-auto px-6 md:px-12 pb-20 text-center">
        <h2 className="font-display text-3xl md:text-4xl font-semibold mb-4">
          You don&apos;t have to carry it all in your head.
        </h2>
        <p className="text-[#7A736E] mb-7 max-w-xl mx-auto">
          Hope Chest takes about 90 seconds to set up. Start with one loved one, free forever.
        </p>
        <Button
          onClick={goSignup}
          className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] px-8 py-6 text-base font-medium"
          data-testid="pricing-final-cta"
        >
          Get started free
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </section>
    </div>
  );
}
